import { createContext, useContext, useEffect, useState, useCallback } from "react";
import {
  shopifyFetch,
  CART_CREATE,
  CART_QUERY,
  CART_LINES_ADD,
  CART_LINES_UPDATE,
  CART_LINES_REMOVE,
} from "@/lib/shopify";

const CartContext = createContext(null);
const STORAGE_KEY = "woc_cart_id";

export function CartProvider({ children }) {
  const [cart, setCart] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [cartError, setCartError] = useState(null);

  useEffect(() => {
    const id = localStorage.getItem(STORAGE_KEY);
    if (!id) return;
    shopifyFetch(CART_QUERY, { id })
      .then((data) => {
        if (data.cart) setCart(data.cart);
        else localStorage.removeItem(STORAGE_KEY);
      })
      .catch(() => localStorage.removeItem(STORAGE_KEY));
  }, []);

  const ensureCart = useCallback(async () => {
    if (cart?.id) return cart.id;
    const data = await shopifyFetch(CART_CREATE, { lines: [] });
    const newCart = data.cartCreate.cart;
    localStorage.setItem(STORAGE_KEY, newCart.id);
    setCart(newCart);
    return newCart.id;
  }, [cart]);

  const addToCart = useCallback(
    async (merchandiseId, quantity = 1) => {
      setBusy(true);
      setCartError(null);
      try {
        const cartId = await ensureCart();
        const data = await shopifyFetch(CART_LINES_ADD, {
          cartId,
          lines: [{ merchandiseId, quantity }],
        });
        const errs = data.cartLinesAdd.userErrors;
        if (errs?.length) throw new Error(errs[0].message);
        setCart(data.cartLinesAdd.cart);
        setIsOpen(true);
      } catch (e) {
        setCartError(e.message);
      } finally {
        setBusy(false);
      }
    },
    [ensureCart]
  );

  const updateLine = useCallback(
    async (lineId, quantity) => {
      if (!cart?.id) return;
      setBusy(true);
      try {
        const data = await shopifyFetch(CART_LINES_UPDATE, {
          cartId: cart.id,
          lines: [{ id: lineId, quantity }],
        });
        setCart(data.cartLinesUpdate.cart);
      } catch (e) {
        setCartError(e.message);
      } finally {
        setBusy(false);
      }
    },
    [cart]
  );

  const removeLine = useCallback(
    async (lineId) => {
      if (!cart?.id) return;
      setBusy(true);
      try {
        const data = await shopifyFetch(CART_LINES_REMOVE, {
          cartId: cart.id,
          lineIds: [lineId],
        });
        setCart(data.cartLinesRemove.cart);
      } catch (e) {
        setCartError(e.message);
      } finally {
        setBusy(false);
      }
    },
    [cart]
  );

  const value = {
    cart,
    isOpen,
    busy,
    cartError,
    openCart: () => setIsOpen(true),
    closeCart: () => setIsOpen(false),
    addToCart,
    updateLine,
    removeLine,
    count: cart?.totalQuantity || 0,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export const useCart = () => useContext(CartContext);
